/**
 * Virtualized masonry grid (frontend-architecture §2). Layout math lives in ./layout.ts and
 * ./visible.ts; this component wires scroll/resize/rAF, slot recycling, scroll anchoring,
 * selection (click/shift/cmd/marquee), keyboard navigation, drag-start and infinite scroll.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { GRID_GAP, GRID_FOOTER, OVERSCAN_ABOVE, OVERSCAN_BELOW, PREFETCH_DISTANCE } from '@/config/constants';
import { useGridPrefs } from '@/stores/gridPrefs';
import { useSelection } from '@/stores/selection';
import { isEditableTarget } from '@/lib/keyboard';
import { modKey } from '@/lib/format';
import { AssetCard, type CardHandlers } from './AssetCard';
import { LayoutCache, isAppendOf, type Layout } from './layout';
import { indicesInRect, neighborIndex, visibleIndices } from './visible';
import { SlotPool, poolSizeFor } from './slots';
import { setAssetDragData } from './dnd';
import { useScrollContainer } from './ScrollContainer';
import { isPlaceholder, type GridItem } from './types';

export type MasonryGridProps = {
  items: GridItem[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  onOpen: (item: GridItem, index: number) => void;
  onQuickLook?: (item: GridItem | null) => void;
  onContextMenu?: (pos: { x: number; y: number }, item: GridItem, index: number) => void;
  onRetryPlaceholder?: (clientUploadId: string) => void;
  onDismissPlaceholder?: (clientUploadId: string) => void;
  sourceBoardId?: string | null;
  selectable?: boolean;
  draggable?: boolean;
  /** override card width target (perf page); defaults to gridPrefs */
  targetCardWidth?: number;
  showTitles?: boolean;
  emptyState?: ReactNode;
  footer?: ReactNode;
  label?: string;
  /** called after each committed frame with timing info (perf page). */
  onFrame?: (info: { mounted: number; wanted: number; layoutMs: number }) => void;
  /** per-card decoration (search results). Keep referentially stable. */
  cardExtra?: CardHandlers['extra'];
};

type Frame = { slots: Int32Array; layout: Layout };

export function MasonryGrid(props: MasonryGridProps) {
  const { items, hasNextPage, isFetchingNextPage, fetchNextPage, onQuickLook, selectable = true, draggable = true, emptyState, footer, label, onFrame, cardExtra } = props;
  const container = useScrollContainer();
  const prefs = useGridPrefs();
  const targetCardWidth = props.targetCardWidth ?? prefs.targetCardWidth;
  const showTitles = props.showTitles ?? prefs.showTitles;
  const footerH = showTitles ? GRID_FOOTER : 0;

  const rootRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [vh, setVh] = useState(() => (typeof window === 'undefined' ? 800 : window.innerHeight));

  // engine state (mutable, never read during render except through `frame`)
  const eng = useRef({
    cache: new LayoutCache(),
    pool: new SlotPool(0),
    out: [] as number[],
    lastWanted: [] as number[],
    itemsVersion: 0,
    prevItems: null as GridItem[] | null,
    scrollTop: 0,
    gridTop: 0,
    raf: 0,
    prevLayout: null as Layout | null,
    /** layout last handed to setFrame */
    frameLayout: null as Layout | null,
    prevScrollTop: 0,
    lastSlotKey: new Map<number, string>(),
    lastSlotH: new Map<number, number>(),
    fetching: false,
  }).current;

  // ----- items version / append detection (§2.3) ------------------------------------------------
  const prevItemsForMemo = eng.prevItems;
  const versionInfo = useMemo(() => {
    if (prevItemsForMemo === items) return { version: eng.itemsVersion, append: true, prev: prevItemsForMemo };
    const append = isAppendOf(prevItemsForMemo, items);
    return { version: append ? eng.itemsVersion : eng.itemsVersion + 1, append, prev: prevItemsForMemo };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // ----- layout (§2.2/2.3) -------------------------------------------------------------------------
  const layoutStart = performance.now();
  const layout = useMemo(
    () => eng.cache.get(items, { containerWidth: Math.max(1, width), targetCardWidth, gap: GRID_GAP, footer: footerH }, versionInfo.version),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, width, targetCardWidth, footerH, versionInfo.version],
  );
  const layoutMs = performance.now() - layoutStart;

  const [frame, setFrame] = useState<Frame>(() => ({ slots: new Int32Array(0), layout }));

  // ----- selection store wiring ----------------------------------------------------------------------
  const setOrder = useSelection((s) => s.setOrder);
  useEffect(() => {
    if (selectable) setOrder(items.map((i) => i.key));
  }, [items, selectable, setOrder]);
  const selectedIds = useSelection((s) => s.ids);
  const focusIndex = useSelection((s) => s.focusIndex);

  // ----- frame computation (§2.4/2.5) ----------------------------------------------------------------
  const computeFrame = useCallback(
    (l: Layout) => {
      const top = eng.scrollTop - OVERSCAN_ABOVE * vh;
      const bottom = eng.scrollTop + vh + OVERSCAN_BELOW * vh;
      const wanted = visibleIndices(l, top, bottom, eng.out);
      const size = poolSizeFor(vh, l.colWidth, l.footer, l.cols);
      if (eng.pool.size < size || eng.pool.size > size * 2) eng.pool.resize(Math.max(size, wanted.length));
      const changed = eng.pool.assign(wanted);
      eng.lastWanted = wanted.slice();
      // compare against the layout the frame actually holds (not `prevLayout`, which the layout-change effect
      // assigns right before calling us): a geometry change with an unchanged slot assignment must still re-render
      if (changed || l !== eng.frameLayout) {
        eng.frameLayout = l;
        setFrame({ slots: eng.pool.slots.slice(), layout: l });
      }
      // prefetch (§2.12)
      if (hasNextPage && !isFetchingNextPage && fetchNextPage && l.height - (eng.scrollTop + vh) < PREFETCH_DISTANCE * vh && !eng.fetching) {
        eng.fetching = true;
        fetchNextPage();
      }
    },
    [eng, vh, hasNextPage, isFetchingNextPage, fetchNextPage],
  );
  useEffect(() => {
    if (!isFetchingNextPage) eng.fetching = false;
  }, [isFetchingNextPage, eng]);

  // Latest-ref: the rAF callback and the ResizeObserver below (created once per container) must always see the
  // current geometry. Capturing `width` by closure framed the grid with the first render's width 0 (= 1px
  // columns) on every resize, and the resulting root height change re-triggered the observer, so the grid
  // settled on the 1px layout (found during the public preview).
  const latest = useRef({ items, width, targetCardWidth, footerH, computeFrame });
  latest.current = { items, width, targetCardWidth, footerH, computeFrame };

  const schedule = useCallback(() => {
    if (eng.raf) return;
    eng.raf = requestAnimationFrame(() => {
      eng.raf = 0;
      const p = latest.current;
      p.computeFrame(eng.cache.get(p.items, { containerWidth: Math.max(1, p.width), targetCardWidth: p.targetCardWidth, gap: GRID_GAP, footer: p.footerH }, eng.itemsVersion));
    });
  }, [eng]);

  // ----- scroll listener (§2.6) ------------------------------------------------------------------------
  const readScroll = useCallback(() => {
    if (!container || !rootRef.current) return;
    eng.gridTop = rootRef.current.offsetTop;
    eng.scrollTop = container.scrollTop - eng.gridTop;
  }, [container, eng]);
  const readScrollRef = useRef(readScroll);
  readScrollRef.current = readScroll;

  useEffect(() => {
    if (!container) return;
    const onScroll = () => {
      readScroll();
      schedule();
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    readScroll();
    return () => container.removeEventListener('scroll', onScroll);
  }, [container, readScroll, schedule]);

  // ----- resize observers ----------------------------------------------------------------------------
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        if (e.target === root) {
          const w = Math.floor(e.contentRect.width);
          if (w > 0) setWidth((prev) => (prev === w ? prev : w));
        } else if (e.target === container) {
          const h = Math.round(e.contentRect.height);
          if (h > 0) setVh((prev) => (prev === h ? prev : h));
        }
      }
      readScrollRef.current();
      schedule();
    });
    ro.observe(root);
    if (container) ro.observe(container);
    const w = Math.floor(root.getBoundingClientRect().width);
    if (w > 0) setWidth(w);
    if (container) setVh(container.clientHeight || vh);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container]);

  // ----- layout change: scroll anchoring (§2.8) + slot remap + synchronous frame ------------------------
  useLayoutEffect(() => {
    const prev = eng.prevLayout;
    const prevItems = versionInfo.prev;
    const keyChanged = !prev || prev.key !== layout.key;
    if (keyChanged && prev && prevItems && !versionInfo.append) {
      // remap slots by key so cards that still exist keep their DOM
      const keyIndex = new Map<string, number>();
      for (let i = 0; i < items.length; i++) keyIndex.set(items[i]!.key, i);
      eng.pool.remap((old) => {
        const k = prevItems[old]?.key;
        return k === undefined ? -1 : (keyIndex.get(k) ?? -1);
      });
      // anchoring
      const st = eng.scrollTop;
      let anchor = -1;
      for (const i of eng.lastWanted) {
        const p = prev.positions[i];
        if (p && p.y >= st) {
          anchor = i;
          break;
        }
      }
      const isPrepend = items.length > prevItems.length && prevItems[0] !== undefined && keyIndex.get(prevItems[0].key) !== 0;
      if (anchor >= 0 && container && st > 0 && !(isPrepend && st <= vh)) {
        const key = prevItems[anchor]?.key;
        const ni = key === undefined ? undefined : keyIndex.get(key);
        const op = prev.positions[anchor];
        const np = ni === undefined ? undefined : layout.positions[ni];
        if (op && np) {
          const offset = op.y - st;
          const target = np.y - offset;
          eng.scrollTop = target;
          container.scrollTop = target + eng.gridTop;
        }
      }
    } else if (keyChanged && prev && versionInfo.append === false && !prevItems) {
      eng.pool.reset();
    } else if (keyChanged && prev && versionInfo.append) {
      // geometry change (zoom/resize) with same items: anchor on first visible item
      const st = eng.scrollTop;
      let anchor = -1;
      for (const i of eng.lastWanted) {
        const p = prev.positions[i];
        if (p && p.y >= st) {
          anchor = i;
          break;
        }
      }
      if (anchor >= 0 && container && st > 0) {
        const op = prev.positions[anchor]!;
        const np = layout.positions[anchor];
        if (np) {
          const target = np.y - (op.y - st);
          eng.scrollTop = target;
          container.scrollTop = target + eng.gridTop;
        }
      }
    }
    eng.prevLayout = layout;
    eng.prevItems = items;
    eng.itemsVersion = versionInfo.version;
    computeFrame(layout);
  }, [layout, items, versionInfo, computeFrame, container, eng, vh]);

  useEffect(() => {
    if (onFrame) onFrame({ mounted: countBound(frame.slots), wanted: eng.lastWanted.length, layoutMs });
  }, [frame, onFrame, eng, layoutMs]);

  // ----- selection + keyboard (§2.10) ----------------------------------------------------------------
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const propsRef = useRef(props);
  propsRef.current = props;
  // L9: `handlers` is memoised on [selectable, cardExtra]; the key handler must still see the current render's closures
  const handleKeyRef = useRef<(e: KeyboardEvent<HTMLElement>, index: number) => void>(() => undefined);

  const scrollIndexIntoView = useCallback(
    (i: number) => {
      const l = layoutRef.current;
      const p = l.positions[i];
      if (!p || !container) return;
      const top = p.y;
      const bottom = p.y + p.h;
      if (top < eng.scrollTop) container.scrollTop = top + eng.gridTop - 8;
      else if (bottom > eng.scrollTop + vh) container.scrollTop = bottom - vh + eng.gridTop + 8;
    },
    [container, eng, vh],
  );

  const focusCard = useCallback(
    (i: number) => {
      useSelection.getState().setFocus(i);
      scrollIndexIntoView(i);
      readScroll();
      computeFrame(layoutRef.current);
      requestAnimationFrame(() => {
        const el = rootRef.current?.querySelector<HTMLElement>(`[data-index="${i}"]`);
        el?.focus({ preventScroll: true });
      });
    },
    [scrollIndexIntoView, readScroll, computeFrame],
  );

  const handlers = useMemo<CardHandlers>(
    () => ({
      extra: cardExtra,
      onClick: (e, index) => {
        if (!selectable) return;
        const item = itemsRef.current[index];
        if (!item) return;
        const sel = useSelection.getState();
        const target = e.target as HTMLElement;
        if (target.closest('[data-select-toggle]')) {
          sel.toggle(item.key, index);
          e.preventDefault();
          return;
        }
        if (e.shiftKey) sel.rangeTo(index);
        else if (modKey(e)) sel.toggle(item.key, index);
        else sel.select(item.key, index);
      },
      onDoubleClick: (index) => {
        const item = itemsRef.current[index];
        if (item && !isPlaceholder(item.card)) propsRef.current.onOpen(item, index);
      },
      onContextMenu: (e, index) => {
        const item = itemsRef.current[index];
        if (!item || !propsRef.current.onContextMenu) return;
        e.preventDefault();
        const sel = useSelection.getState();
        if (!sel.ids.has(item.key)) sel.select(item.key, index);
        propsRef.current.onContextMenu({ x: e.clientX, y: e.clientY }, item, index);
      },
      onDragStart: (e, index) => {
        const item = itemsRef.current[index];
        if (!item || isPlaceholder(item.card)) return;
        const sel = useSelection.getState();
        if (!sel.ids.has(item.key)) sel.select(item.key, index);
        const ids = [...useSelection.getState().ids].filter((k) => !k.startsWith('cu_'));
        const thumb = !isPlaceholder(item.card) ? (item.card.thumbnails['256'] ?? item.card.thumbnails['512']) : null;
        setAssetDragData(e, { ids, source_board_id: propsRef.current.sourceBoardId ?? null }, thumb, e.altKey);
      },
      onKeyDown: (e, index) => handleKeyRef.current(e, index),
      onRetryPlaceholder: (id) => propsRef.current.onRetryPlaceholder?.(id),
      onDismissPlaceholder: (id) => propsRef.current.onDismissPlaceholder?.(id),
    }),
    [selectable, cardExtra],
  );

  function handleKey(e: KeyboardEvent<HTMLElement>, index: number) {
    const l = layoutRef.current;
    const sel = useSelection.getState();
    const item = itemsRef.current[index];
    let dir: 'left' | 'right' | 'up' | 'down' | null;
    switch (e.key) {
      case 'ArrowLeft':
        dir = 'left';
        break;
      case 'ArrowRight':
        dir = 'right';
        break;
      case 'ArrowUp':
        dir = 'up';
        break;
      case 'ArrowDown':
        dir = 'down';
        break;
      case 'Enter':
        if (item && !isPlaceholder(item.card)) {
          e.preventDefault();
          propsRef.current.onOpen(item, index);
        }
        return;
      case ' ':
        if (item && !isPlaceholder(item.card) && onQuickLook) {
          e.preventDefault();
          onQuickLook(item);
        }
        return;
      case 'Home':
        e.preventDefault();
        focusCard(0);
        return;
      case 'End':
        e.preventDefault();
        focusCard(itemsRef.current.length - 1);
        return;
      default:
        return;
    }
    e.preventDefault();
    const next = neighborIndex(l, index, dir);
    if (next < 0) return;
    const nextItem = itemsRef.current[next];
    if (!nextItem) return;
    if (selectable) {
      if (e.shiftKey) sel.rangeTo(next);
      else if (!modKey(e)) sel.select(nextItem.key, next);
    }
    focusCard(next);
  }
  useLayoutEffect(() => {
    handleKeyRef.current = handleKey;
  });

  // Cmd/Ctrl+A and Esc at document level while the grid is mounted
  useEffect(() => {
    if (!selectable) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (isEditableTarget(e)) return;
      // the board grid stays mounted under the asset overlay (§1 background routing); keys belong to the overlay then
      if (document.querySelector('[data-asset-overlay]')) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        useSelection.getState().selectAll();
      } else if (e.key === 'Escape') {
        const s = useSelection.getState();
        if (s.ids.size > 0) {
          e.preventDefault();
          e.stopImmediatePropagation();
          s.clear();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [selectable]);

  // ----- marquee (§2.10) ------------------------------------------------------------------------------
  const [marquee, setMarquee] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const marqueeRef = useRef<{ x1: number; y1: number; base: Set<string>; raf: number; last: PointerEvent<HTMLDivElement> | { clientX: number; clientY: number; altKey: boolean } | null; hits: number[] } | null>(null);

  const toGridCoords = useCallback(
    (clientX: number, clientY: number) => {
      const root = rootRef.current;
      if (!root) return { x: 0, y: 0 };
      const r = root.getBoundingClientRect();
      return { x: clientX - r.left, y: clientY - r.top };
    },
    [],
  );

  const onRootPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!selectable || e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest('article')) return; // card handles itself
      if (target !== rootRef.current && !target.hasAttribute('data-grid-bg')) return;
      const { x, y } = toGridCoords(e.clientX, e.clientY);
      const sel = useSelection.getState();
      marqueeRef.current = { x1: x, y1: y, base: e.altKey ? new Set(sel.ids) : new Set(), raf: 0, last: null, hits: [] };
      if (!e.altKey && !modKey(e)) sel.clear();
      rootRef.current?.setPointerCapture(e.pointerId);
      setMarquee({ x1: x, y1: y, x2: x, y2: y });
    },
    [selectable, toGridCoords],
  );
  const onRootPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      const m = marqueeRef.current;
      if (!m) return;
      m.last = { clientX: e.clientX, clientY: e.clientY, altKey: e.altKey };
      if (m.raf) return;
      m.raf = requestAnimationFrame(() => {
        m.raf = 0;
        const last = m.last;
        if (!last) return;
        // auto-scroll near edges
        if (container) {
          const cr = container.getBoundingClientRect();
          if (last.clientY > cr.bottom - 24) container.scrollTop += 16;
          else if (last.clientY < cr.top + 24) container.scrollTop -= 16;
        }
        const { x, y } = toGridCoords(last.clientX, last.clientY);
        setMarquee({ x1: m.x1, y1: m.y1, x2: x, y2: y });
        const hits = indicesInRect(layoutRef.current, m.x1, m.y1, x, y, m.hits);
        const keys: string[] = [];
        for (const i of hits) {
          const it = itemsRef.current[i];
          if (it) keys.push(it.key);
        }
        const all = last.altKey || m.base.size ? [...m.base, ...keys] : keys;
        useSelection.getState().addMany(all, true);
      });
    },
    [container, toGridCoords],
  );
  const onRootPointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const m = marqueeRef.current;
    if (!m) return;
    if (m.raf) cancelAnimationFrame(m.raf);
    marqueeRef.current = null;
    setMarquee(null);
    try {
      rootRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  // ----- render --------------------------------------------------------------------------------------
  const { slots, layout: fl } = frame;
  const slotEls: ReactNode[] = [];
  const nextKeys = new Map<number, string>();
  for (let s = 0; s < slots.length; s++) {
    const i = slots[s]!;
    const item = i >= 0 ? items[i] : undefined;
    const p = item ? fl.positions[i] : undefined;
    if (!item || !p) {
      slotEls.push(<div key={s} className="grid-slot" style={{ display: 'none' }} />);
      continue;
    }
    const prevKey = eng.lastSlotKey.get(s);
    const prevH = eng.lastSlotH.get(s);
    const animate = prevKey === item.key && prevH !== undefined && Math.abs(prevH - p.h) > 0.5;
    nextKeys.set(s, item.key);
    eng.lastSlotH.set(s, p.h);
    slotEls.push(
      <div key={s} className="grid-slot" data-animate={animate ? '1' : undefined} style={{ transform: `translate3d(${Math.round(p.x)}px, ${Math.round(p.y)}px, 0)`, width: Math.round(p.w), height: Math.round(p.h) }}>
        <AssetCard item={item} index={i} w={Math.round(p.w)} h={Math.round(p.h)} selected={selectedIds.has(item.key)} focused={focusIndex === i} showTitle={showTitles} draggable={draggable} handlers={handlers} />
      </div>,
    );
  }
  eng.lastSlotKey = nextKeys;

  const empty = items.length === 0;
  return (
    <div
      ref={rootRef}
      role="grid"
      aria-label={label ?? 'Assets'}
      aria-rowcount={items.length}
      data-grid-bg
      className="relative w-full"
      style={{ height: empty ? undefined : Math.max(fl.height, 1), minHeight: empty ? 120 : undefined, touchAction: 'pan-y' }}
      onPointerDown={onRootPointerDown}
      onPointerMove={marquee ? onRootPointerMove : undefined}
      onPointerUp={marquee ? onRootPointerUp : undefined}
      onPointerCancel={marquee ? onRootPointerUp : undefined}
      onContextMenu={(e: MouseEvent) => {
        if ((e.target as HTMLElement).closest('article')) return;
      }}
      onDragOver={(e: DragEvent) => e.preventDefault()}
    >
      {empty ? emptyState : slotEls}
      {marquee && (
        <div
          aria-hidden
          className="pointer-events-none absolute z-10 border border-accent bg-accent/15"
          style={{ left: Math.min(marquee.x1, marquee.x2), top: Math.min(marquee.y1, marquee.y2), width: Math.abs(marquee.x2 - marquee.x1), height: Math.abs(marquee.y2 - marquee.y1) }}
        />
      )}
      {!empty && footer && <div className="absolute right-0 left-0" style={{ top: fl.height }}>{footer}</div>}
    </div>
  );
}

function countBound(slots: Int32Array): number {
  let n = 0;
  for (let i = 0; i < slots.length; i++) if (slots[i]! >= 0) n++;
  return n;
}
